import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpen, ChevronLeft, ChevronRight, X } from "lucide-react";
import type { TutorPageAsset } from "../../modules/tutor/api/tutor.client";

export type TutorGrade3TextbookPanelProps = {
  assets: TutorPageAsset[];
  pageIndex: number;
  onPrev: () => void;
  onNext: () => void;
};

/**
 * Textbook page preview for AI Tutor (Grade 3 only). Uses `image_url` from the API as-is (public paths).
 * Full-size view: portaled to `document.body`, dimmed + blurred backdrop (layout faintly visible) — page image + minimal controls only.
 */
export function TutorGrade3TextbookPanel({ assets, pageIndex, onPrev, onNext }: TutorGrade3TextbookPanelProps) {
  const [popupOpen, setPopupOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!assets.length) return null;
  const safeIx = Math.max(0, Math.min(pageIndex, assets.length - 1));
  const cur = assets[safeIx];
  const multi = assets.length > 1;

  useEffect(() => {
    if (!popupOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPopupOpen(false);
      if (multi && e.key === "ArrowLeft") onPrev();
      if (multi && e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [popupOpen, multi, onPrev, onNext]);

  return (
    <>
      <div className="flex flex-col items-stretch w-full max-w-[220px] sm:max-w-[260px] shrink-0">
        <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide text-slate-600 mb-1.5">
          <BookOpen size={12} className="text-emerald-700 shrink-0" aria-hidden />
          <span>Textbook</span>
          {multi && (
            <span className="ml-auto font-mono text-slate-500 normal-case font-semibold">
              {safeIx + 1}/{assets.length}
            </span>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setPopupOpen(true)}
            className="group relative block w-full text-left cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            aria-label={`Open textbook page ${cur.page_no} full size`}
          >
            <div className="aspect-[3/4] bg-slate-100 flex items-center justify-center">
              <img
                src={cur.image_url}
                alt={`Textbook page ${cur.page_no}`}
                className="w-full h-full object-contain pointer-events-none"
                loading="lazy"
              />
            </div>
            <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/55 px-1.5 py-0.5 text-[9px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
              Tap to enlarge
            </span>
          </button>
          {multi && (
            <div className="flex border-t border-slate-200">
              <button
                type="button"
                onClick={onPrev}
                disabled={safeIx <= 0}
                className="flex-1 flex items-center justify-center py-2 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700"
                aria-label="Previous textbook page"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={safeIx >= assets.length - 1}
                className="flex-1 flex items-center justify-center py-2 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 border-l border-slate-200"
                aria-label="Next textbook page"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
        <p className="mt-1 text-[10px] text-slate-500 text-center font-mono">Page {cur.page_no}</p>
      </div>

      {popupOpen &&
        mounted &&
        createPortal(
          <div
            className="fixed inset-0 z-[2147483000] flex min-h-[100dvh] w-screen items-center justify-center bg-slate-950/50 backdrop-blur-md"
            style={{ isolation: "isolate" }}
            role="dialog"
            aria-modal="true"
            aria-label={`Textbook page ${cur.page_no}`}
            onClick={() => setPopupOpen(false)}
          >
            <span className="sr-only">Textbook page {cur.page_no}. Press Escape to close.</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPopupOpen(false);
              }}
              className="absolute right-3 top-3 z-[2147483001] flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg ring-1 ring-black/20 transition hover:bg-slate-100 sm:right-5 sm:top-5"
              aria-label="Close"
            >
              <X size={24} />
            </button>
            {multi && safeIx > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrev();
                }}
                className="absolute left-2 top-1/2 z-[2147483001] -translate-y-1/2 rounded-full bg-white/15 p-3 text-white backdrop-blur-sm transition hover:bg-white/25 sm:left-4"
                aria-label="Previous page"
              >
                <ChevronLeft size={28} />
              </button>
            )}
            {multi && safeIx < assets.length - 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNext();
                }}
                className="absolute right-2 top-1/2 z-[2147483001] -translate-y-1/2 rounded-full bg-white/15 p-3 text-white backdrop-blur-sm transition hover:bg-white/25 sm:right-4 md:right-[4.5rem]"
                aria-label="Next page"
              >
                <ChevronRight size={28} />
              </button>
            )}
            <div
              className="flex max-h-[100dvh] max-w-[100vw] items-center justify-center px-2 pb-2 pt-14 sm:px-6 sm:pb-4 sm:pt-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={cur.image_url}
                alt=""
                className="max-h-[calc(100dvh-5rem)] max-w-[calc(100vw-1rem)] object-contain sm:max-h-[calc(100dvh-4.5rem)] sm:max-w-[calc(100vw-2rem)]"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
